import { useEffect, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Loader2, Copy, Download, Sparkles, Check } from "lucide-react";

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed — long-press to copy manually");
  }
};

function KitContent({ coin, kit, hasBanner }) {
  return (
    <div data-testid="launch-kit-content">
      {hasBanner && (
        <div className="mb-6">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
            Promo banner
          </div>
          <img
            src={`${BACKEND_URL}/api/coins/${coin.slug}/launch-kit/banner`}
            alt={`${coin.name} banner`}
            className="w-full max-w-xl border border-[#3a3a3a]"
            data-testid="kit-banner"
          />
          <a
            href={`${BACKEND_URL}/api/coins/${coin.slug}/launch-kit/banner`}
            download={`${coin.slug}-banner.png`}
            target="_blank"
            rel="noreferrer"
            data-testid="kit-banner-download"
            className="inline-flex items-center gap-1.5 mt-2 font-mono text-[11px] uppercase tracking-widest text-[#cfff04] hover:underline"
          >
            <Download size={11} /> Download banner
          </a>
        </div>
      )}

      <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
        6-tweet launch thread
      </div>
      <ol className="space-y-2 mb-6">
        {(kit.thread || []).map((t, i) => (
          <li
            key={t.slice(0, 40)}
            className="border border-[#3a3a3a] bg-[#050505] p-3 flex items-start justify-between gap-3"
            data-testid={`kit-tweet-${i}`}
          >
            <span className="text-sm text-[#d4d4d4]">
              <span className="font-mono text-[11px] text-[#d0d0d0] mr-2">{i + 1}/</span>
              {t}
            </span>
            <button
              onClick={() => copyText(t, `Tweet ${i + 1}`)}
              data-testid={`kit-tweet-copy-${i}`}
              className="flex-shrink-0 border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#d0d0d0] p-1.5"
            >
              <Copy size={12} />
            </button>
          </li>
        ))}
      </ol>

      <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
        Telegram / Discord pitch
      </div>
      <div className="border border-[#3a3a3a] bg-[#050505] p-3 flex items-start justify-between gap-3 mb-6" data-testid="kit-telegram">
        <span className="text-sm text-[#d4d4d4]">{kit.telegram_pitch}</span>
        <button
          onClick={() => copyText(kit.telegram_pitch, "Pitch")}
          data-testid="kit-telegram-copy"
          className="flex-shrink-0 border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#d0d0d0] p-1.5"
        >
          <Copy size={12} />
        </button>
      </div>

      {(kit.memes || []).length > 0 && (
        <>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
            Meme captions
          </div>
          <ul className="space-y-1.5" data-testid="kit-memes">
            {kit.memes.map((m) => (
              <li key={m.slice(0, 40)} className="text-sm text-[#e2e2e2]">
                → {m}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function LaunchKit({ coin }) {
  const [state, setState] = useState(null); // {paid, kit, banner}
  const [buyBusy, setBuyBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/${coin.slug}/launch-kit`);
      setState(data);
    } catch (e) {
      console.warn("launch kit load failed", e?.message);
      setState(null);
    }
  }, [coin.slug]);

  useEffect(() => {
    load();
  }, [load]);

  const buy = async () => {
    setBuyBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        sku: "LAUNCH_KIT",
        coin_slug: coin.slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBuyBusy(false);
      toast.error(e.response?.data?.detail || "Payment failed — try again");
    }
  };

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/coins/${coin.slug}/launch-kit/generate`);
      toast.success(data.cached ? "Kit ready" : "Launch Kit generated!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed — try again");
    } finally {
      setGenBusy(false);
    }
  };

  if (!state) return null;

  return (
    <section className="border border-[#3a3a3a] bg-[#121212] p-6 mb-6" data-testid="launch-kit-section">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-3">
        // AI Launch Kit
      </div>

      {!state.paid ? (
        <div className="grid grid-cols-12 gap-4 items-center">
          <div className="col-span-12 md:col-span-8">
            <div className="font-display text-2xl font-bold uppercase mb-1">
              Marketing, written for you. <span className="text-[#cfff04]">$9</span>
            </div>
            <ul className="text-sm text-[#e2e2e2] space-y-1 mt-3">
              <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> 6-tweet launch thread, hook-first, ready to post</li>
              <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> AI promo banner with your ${coin.ticker} branding</li>
              <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Telegram / Discord pitch + 3 meme captions</li>
            </ul>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <button
              onClick={buy}
              disabled={buyBusy}
              data-testid="launch-kit-buy"
              className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-6 py-3 inline-flex items-center gap-2"
            >
              {buyBusy ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />}
              Get the kit · $9
            </button>
          </div>
        </div>
      ) : !state.kit ? (
        <div className="text-center py-6" data-testid="launch-kit-ready-to-generate">
          <div className="font-display text-2xl font-bold uppercase mb-2">
            Kit unlocked. <span className="text-[#00ff41]">Let AI cook.</span>
          </div>
          <p className="text-sm text-[#e2e2e2] mb-5 max-w-md mx-auto">
            One click generates your tweet thread, promo banner, and pitches. Takes ~30 seconds.
          </p>
          <button
            onClick={generate}
            disabled={genBusy}
            data-testid="launch-kit-generate"
            className="btn-brutal bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-sm px-7 py-4 inline-flex items-center gap-2"
          >
            {genBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {genBusy ? "AI is writing…" : "Generate my Launch Kit"}
          </button>
        </div>
      ) : (
        <KitContent coin={coin} kit={state.kit} hasBanner={state.banner} />
      )}
    </section>
  );
}
